import React from "react";
import { Link } from "react-router-dom";
import { X, BarChart2 } from "lucide-react";
import { useApp } from "../context/AppContext";
import { formatPrice } from "../components/ProductCard";
import { priceInfo } from "../mock";

export default function Compare() {
  const { t, tr, lang, products, compare, toggleCompare, clearCompare } = useApp();
  const items = products.filter((p) => compare.includes(p.id));

  if (items.length === 0) {
    return (
      <div className="min-h-[60vh] max-w-[1400px] mx-auto px-4 md:px-8 py-16 text-center">
        <h1 className="brand-font text-4xl tracking-wide mb-2">{t("comparePage")}</h1>
        <div className="w-10 h-[2px] mx-auto mb-10" style={{ backgroundColor: "var(--brand)" }} />
        <BarChart2 size={40} className="mx-auto text-neutral-300 mb-4" />
        <p className="text-neutral-500 mb-6">{t("emptyCompare")}</p>
        <Link to="/shop" className="inline-block text-white text-xs tracking-widest uppercase px-8 py-3.5" style={{ backgroundColor: "var(--brand)" }}>{t("startShopping")}</Link>
      </div>
    );
  }

  const rows = [
    { key: "specPrice", render: (p) => { const pi = priceInfo(p); return pi.hasDiscount ? <span><span className="line-through mr-1" style={{ color: "#c1121f" }}>{formatPrice(pi.original)}</span><b>{formatPrice(pi.final)}</b></span> : <b>{formatPrice(pi.final)}</b>; } },
    { key: "specRating", render: (p) => `${p.rating.toFixed(1)} / 5` },
    { key: "specBrand", render: (p) => p.specs?.brand || "-" },
    { key: "specMovement", render: (p) => tr(p.specs?.movement) || "-" },
    { key: "specCase", render: (p) => tr(p.specs?.caseMaterial) || "-" },
    { key: "specDiameter", render: (p) => p.specs?.diameter || "-" },
    { key: "specGlass", render: (p) => tr(p.specs?.glass) || "-" },
    { key: "specWater", render: (p) => p.specs?.waterResistance || "-" },
    { key: "specWarranty", render: (p) => tr(p.specs?.warranty) || "-" },
  ];

  return (
    <div className="min-h-[60vh] max-w-[1400px] mx-auto px-4 md:px-8 py-12">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="brand-font text-4xl tracking-wide">{t("comparePage")}</h1>
          <div className="w-10 h-[2px] mt-3" style={{ backgroundColor: "var(--brand)" }} />
        </div>
        <button onClick={clearCompare} className="text-xs tracking-widest uppercase text-neutral-500 hover:text-red-600 flex items-center gap-1"><X size={14} /> {t("clearCompare")}</button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border-collapse min-w-[640px]">
          <thead>
            <tr>
              <th className="w-32 md:w-44"></th>
              {items.map((p) => {
                const name = lang === "en" && p.nameEn ? p.nameEn : p.name;
                return (
                  <th key={p.id} className="p-3 align-top border-l border-neutral-100">
                    <div className="relative">
                      <button onClick={() => toggleCompare(p.id)} className="absolute -top-1 -right-1 bg-white border border-neutral-200 rounded-full w-6 h-6 flex items-center justify-center text-neutral-500 hover:text-red-600 z-10"><X size={13} /></button>
                      <Link to={`/product/${p.id}`}>
                        <img src={p.images[0]} alt={name} className="w-full aspect-[3/4] object-cover mb-3" />
                        <span className="block text-sm font-light text-neutral-800 hover:text-[color:var(--brand)] transition-colors">{name}</span>
                      </Link>
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, ri) => (
              <tr key={row.key} className={ri % 2 ? "bg-white" : "bg-neutral-50"}>
                <td className="px-4 py-3 text-[11px] tracking-widest uppercase text-neutral-500 font-medium">{t(row.key)}</td>
                {items.map((p) => (
                  <td key={p.id} className="px-4 py-3 text-sm text-neutral-800 text-center border-l border-neutral-100">{row.render(p)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
